import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sparkles, Image as ImageIcon, Plus } from 'lucide-react';
import { toast } from 'sonner';
import type { ProductSearchResult, ImageSearchResult } from '../services/llmService';
import ImageProxyService from '../services/imageProxyService';
import { logger } from '@/lib/logger';
import { useMergedCategoriesHierarchy, type DomainType } from '../hooks/useMergedCategories';
import { CategoryIconRenderer } from '../utils/categoryIcons';
import { QuickCategoryCreateDialog } from '@/components/portal/QuickCategoryCreateDialog';
import { useQueryClient } from '@tanstack/react-query';

// Helper to coerce empty strings and NaN to undefined for optional numbers
const optionalNumber = z.preprocess(val => {
  if (val === '' || val === null || val === undefined || (typeof val === 'number' && isNaN(val))) {
    return undefined;
  }
  return val;
}, z.number().optional());

const productSchema = z.object({
  brand: z.string().min(1, 'Brand is required'),
  model: z.string().min(1, 'Model is required'),
  part_number: z.string().optional().nullable().default(''),
  category: z.string().min(1, 'Category is required'),
  subcategory: z.string().optional().nullable().default(''),
  description: z.string().optional().nullable().default(''),
  price: optionalNumber,
  currency: z.string().default('USD'),
  // Specifications - all optional
  wattage: optionalNumber,
  lumens: optionalNumber,
  color_temp: z.string().optional().nullable(),
  beam_angle: optionalNumber,
  power_consumption: optionalNumber,
  // Dimensions - all optional
  width: optionalNumber,
  height: optionalNumber,
  depth: optionalNumber,
  dimension_unit: z.string().default('inches'),
  weight_value: optionalNumber,
  weight_unit: z.string().default('kg'),
  // Flags
  is_public: z.boolean().default(false),
  is_verified: z.boolean().default(false),
});

type ProductFormData = z.infer<typeof productSchema>;

interface ProductReviewFormProps {
  initialData: ProductSearchResult;
  selectedImage: ImageSearchResult | null;
  availableImages?: ImageSearchResult[];
  searchQuery?: string;
  onSave: (data: any) => void;
  onCancel: () => void;
  onImageSelect?: (image: ImageSearchResult | null) => void;
  isLoading?: boolean;
}

export function ProductReviewForm({
  initialData,
  selectedImage,
  availableImages = [],
  searchQuery,
  onSave,
  onCancel,
  onImageSelect,
  isLoading = false,
}: ProductReviewFormProps) {
  // Debug: Log initialData specs
  logger.debug('[ProductReviewForm] initialData received:', {
    basic: initialData.basic,
    specifications: initialData.specifications,
    dimensions: initialData.specifications?.dimensions,
    weight: initialData.specifications?.weight,
    power: initialData.specifications?.power,
  });

  const [activeTab, setActiveTab] = useState('basic');
  const [showImageSelector, setShowImageSelector] = useState(false);
  const [customImageUrl, setCustomImageUrl] = useState('');
  const [proxiedImageUrl, setProxiedImageUrl] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState(initialData.basic.category || '');

  const queryClient = useQueryClient();

  // Fetch merged categories for all domains
  const { hierarchy: lightingHierarchy } = useMergedCategoriesHierarchy('lighting_fixtures');
  const { hierarchy: controlsHierarchy } = useMergedCategoriesHierarchy('lighting_controls');
  const { hierarchy: lvHierarchy } = useMergedCategoriesHierarchy('lv_products');

  // Combine parent categories
  const allParentCategories = [
    ...lightingHierarchy.parents,
    ...controlsHierarchy.parents,
    ...lvHierarchy.parents,
  ];

  // State for create dialogs
  const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
  const [createSubcategoryOpen, setCreateSubcategoryOpen] = useState(false);
  const [createDomain, setCreateDomain] = useState<DomainType>('lighting_fixtures');

  // Helper functions
  const getSubcategoriesForCategory = (cat: string) => {
    return [
      ...(lightingHierarchy.children.get(cat) || []),
      ...(controlsHierarchy.children.get(cat) || []),
      ...(lvHierarchy.children.get(cat) || []),
    ];
  };

  const getCategoryTemplate = (cat: string) => allParentCategories.find(c => c.category === cat);

  const subcategories = getSubcategoriesForCategory(selectedCategory);

  // Proxy the selected image to bypass CORS
  useEffect(() => {
    if (selectedImage?.url) {
      logger.log('Proxying image URL:', selectedImage.url);
      ImageProxyService.getProxiedUrl(selectedImage.url)
        .then(proxiedUrl => {
          logger.log('Proxied URL generated:', proxiedUrl);
          setProxiedImageUrl(proxiedUrl);
        })
        .catch(error => {
          logger.error('Failed to proxy image:', error);
          setProxiedImageUrl(selectedImage.url); // Fallback to original
        });
    } else {
      setProxiedImageUrl(null);
    }
  }, [selectedImage]);

  // Prevent spacebar from triggering floor plan pan
  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ') {
      e.stopPropagation();
    }
  };

  // Helper function to format model name - preserve uppercase models
  const formatModelName = (model: string) => {
    if (!model) return '';
    // For models that are all uppercase with hyphens (like PAV-SIPA1SM), preserve as-is
    if (model.includes('-') && model === model.toUpperCase()) {
      return model;
    }
    // Otherwise capitalize first letter of each word
    return model
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      brand: initialData.basic.brand,
      model: formatModelName(initialData.basic.model),
      part_number: initialData.basic.partNumber || initialData.basic.model || '', // Default to model if no part number
      category: initialData.basic.category,
      subcategory: initialData.basic.subcategory,
      description: initialData.basic.description,
      price: initialData.pricing?.msrp,
      currency: initialData.pricing?.currency || 'USD',
      wattage: initialData.specifications.wattage,
      lumens: initialData.specifications.lumens,
      power_consumption: initialData.specifications.power?.consumption,
      width: initialData.specifications.dimensions?.width,
      height: initialData.specifications.dimensions?.height,
      depth: initialData.specifications.dimensions?.depth,
      dimension_unit: initialData.specifications.dimensions?.unit || 'inches', // Changed default to inches
      weight_value: initialData.specifications.weight?.value,
      weight_unit: initialData.specifications.weight?.unit || 'kg',
      is_public: false,
      is_verified: false,
    },
  });

  // Unit conversion functions
  const mmToInches = (mm: number) => mm / 25.4;
  const inchesToMm = (inches: number) => inches * 25.4;
  const kgToLbs = (kg: number) => kg * 2.20462;
  const lbsToKg = (lbs: number) => lbs / 2.20462;

  // Handle dimension unit change with conversion
  const handleDimensionUnitChange = (newUnit: string) => {
    const currentUnit = form.watch('dimension_unit');
    const width = form.watch('width');
    const height = form.watch('height');
    const depth = form.watch('depth');

    if (currentUnit === newUnit) return;

    // Convert values
    if (currentUnit === 'mm' && newUnit === 'inches') {
      if (width) form.setValue('width', Number(mmToInches(width).toFixed(2)));
      if (height) form.setValue('height', Number(mmToInches(height).toFixed(2)));
      if (depth) form.setValue('depth', Number(mmToInches(depth).toFixed(2)));
    } else if (currentUnit === 'inches' && newUnit === 'mm') {
      if (width) form.setValue('width', Number(inchesToMm(width).toFixed(0)));
      if (height) form.setValue('height', Number(inchesToMm(height).toFixed(0)));
      if (depth) form.setValue('depth', Number(inchesToMm(depth).toFixed(0)));
    }

    form.setValue('dimension_unit', newUnit);
  };

  // Handle weight unit change with conversion
  const handleWeightUnitChange = (newUnit: string) => {
    const currentUnit = form.watch('weight_unit');
    const weight = form.watch('weight_value');

    if (currentUnit === newUnit || !weight) return;

    // Convert values
    if (currentUnit === 'kg' && newUnit === 'lbs') {
      form.setValue('weight_value', Number(kgToLbs(weight).toFixed(2)));
    } else if (currentUnit === 'lbs' && newUnit === 'kg') {
      form.setValue('weight_value', Number(lbsToKg(weight).toFixed(2)));
    }

    form.setValue('weight_unit', newUnit);
  };

  const onSubmit = (data: ProductFormData) => {
    logger.log('[ProductReviewForm] Form submitted with data:', data);

    // Convert dimensions to mm if needed for individual columns
    let width_mm: number | undefined;
    let height_mm: number | undefined;
    let depth_mm: number | undefined;

    if (data.dimension_unit === 'inches') {
      width_mm = data.width ? Math.round(data.width * 25.4) : undefined;
      height_mm = data.height ? Math.round(data.height * 25.4) : undefined;
      depth_mm = data.depth ? Math.round(data.depth * 25.4) : undefined;
    } else {
      // Already in mm
      width_mm = data.width ? Math.round(data.width) : undefined;
      height_mm = data.height ? Math.round(data.height) : undefined;
      depth_mm = data.depth ? Math.round(data.depth) : undefined;
    }

    // Convert weight to kg if needed
    let weight_kg: number | undefined;
    if (data.weight_unit === 'lbs') {
      weight_kg = data.weight_value ? data.weight_value / 2.20462 : undefined;
    } else {
      // Already in kg
      weight_kg = data.weight_value;
    }

    // Build payload with proper database column names
    const payload = {
      brand: data.brand,
      model: data.model,
      part_number: data.part_number,
      category: data.category,
      subcategory: data.subcategory,
      description: data.description,
      price: data.price,
      currency: data.currency,
      wattage: data.wattage,
      lumens: data.lumens,
      color_temp: data.color_temp,
      beam_angle: data.beam_angle,
      power_consumption: data.power_consumption,
      is_public: data.is_public,
      is_verified: data.is_verified,
      // Individual dimension columns (in mm)
      width_mm,
      height_mm,
      depth_mm,
      weight_kg,
      // Also store as JSONB for full precision and original units
      dimensions:
        data.width || data.height || data.depth
          ? {
              width: data.width,
              height: data.height,
              depth: data.depth,
              unit: data.dimension_unit,
            }
          : undefined,
      weight: data.weight_value
        ? {
            value: data.weight_value,
            unit: data.weight_unit,
          }
        : undefined,
      // Specifications JSONB for additional fields
      specifications: {
        power: data.power_consumption
          ? {
              consumption: data.power_consumption,
              unit: 'W',
            }
          : undefined,
        ...initialData.specifications,
      },
      // Note: selectedImage is handled separately in AIProductCreationModal, not stored in DB
    };

    logger.log('[ProductReviewForm] Calling onSave with payload:', payload);
    onSave(payload);
  };

  // Log form errors for debugging
  const formErrors = form.formState.errors;
  if (Object.keys(formErrors).length > 0) {
    logger.error('[ProductReviewForm] Form validation errors:', formErrors);
  }

  const confidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Handle form validation errors explicitly
  const onFormError = (errors: any) => {
    logger.error('[ProductReviewForm] Form validation failed:', errors);
    toast.error('Please fill in all required fields: ' + Object.keys(errors).join(', '));
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit, onFormError)} className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setShowImageSelector(true)}
            className="w-20 h-20 bg-muted rounded-lg overflow-hidden flex items-center justify-center hover:ring-2 hover:ring-primary transition-all cursor-pointer group relative"
            title="Click to change image"
          >
            {selectedImage && proxiedImageUrl ? (
              <>
                <img
                  src={proxiedImageUrl}
                  alt="Product"
                  className="w-full h-full object-contain"
                  onError={e => {
                    logger.error('Failed to load proxied image:', proxiedImageUrl);
                    e.currentTarget.src = ImageProxyService.getPlaceholderImage();
                  }}
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ImageIcon className="h-6 w-6 text-white" />
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Click to Add</span>
              </div>
            )}
          </button>
          <div>
            <h3 className="text-lg font-semibold">
              {form.watch('brand')} {form.watch('model')}
            </h3>
            <div className="flex items-center gap-2 text-sm">
              {initialData.provider !== 'manual' && (
                <>
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span>AI Generated</span>
                  <span className={confidenceColor(initialData.confidence)}>
                    ({Math.round(initialData.confidence * 100)}% confidence)
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="basic">Basic Info</TabsTrigger>
          <TabsTrigger value="specs">Specifications</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="brand">Brand *</Label>
              <Input
                id="brand"
                {...form.register('brand')}
                placeholder="SONOS"
                onKeyDown={handleInputKeyDown}
              />
              {form.formState.errors.brand && (
                <p className="text-sm text-red-600">{form.formState.errors.brand.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">Model *</Label>
              <Input
                id="model"
                {...form.register('model')}
                placeholder="Era 300"
                onKeyDown={handleInputKeyDown}
              />
              {form.formState.errors.model && (
                <p className="text-sm text-red-600">{form.formState.errors.model.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="part_number">Part Number</Label>
            <Input
              id="part_number"
              {...form.register('part_number')}
              onKeyDown={handleInputKeyDown}
            />
          </div>

          {/* Category and Subcategory side by side */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={form.watch('category')}
                onValueChange={value => {
                  if (value === '__create_new__') {
                    // Default to lv_products domain for new categories
                    setCreateDomain('lv_products');
                    setCreateCategoryOpen(true);
                  } else {
                    form.setValue('category', value);
                    setSelectedCategory(value);
                    // Reset subcategory when category changes
                    form.setValue('subcategory', '');
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue>
                    {form.watch('category') && (
                      <div className="flex items-center gap-2">
                        <CategoryIconRenderer
                          category={form.watch('category')}
                          template={getCategoryTemplate(form.watch('category'))}
                          size={18}
                        />
                        <span>
                          {getCategoryTemplate(form.watch('category'))?.display_name ||
                            form
                              .watch('category')
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {/* Lighting Fixtures Group */}
                  <SelectGroup>
                    <SelectLabel className="text-xs text-muted-foreground px-2 py-1.5">
                      Lighting Fixtures
                    </SelectLabel>
                    {lightingHierarchy.parents.map(cat => (
                      <SelectItem key={cat.id} value={cat.category}>
                        <div className="flex items-center gap-2">
                          <CategoryIconRenderer category={cat.category} template={cat} size={18} />
                          <span>{cat.display_name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>

                  <SelectSeparator />

                  {/* Controls Group */}
                  <SelectGroup>
                    <SelectLabel className="text-xs text-muted-foreground px-2 py-1.5">
                      Controls
                    </SelectLabel>
                    {controlsHierarchy.parents.map(cat => (
                      <SelectItem key={cat.id} value={cat.category}>
                        <div className="flex items-center gap-2">
                          <CategoryIconRenderer category={cat.category} template={cat} size={18} />
                          <span>{cat.display_name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>

                  <SelectSeparator />

                  {/* Products (LV) Group */}
                  <SelectGroup>
                    <SelectLabel className="text-xs text-muted-foreground px-2 py-1.5">
                      Products
                    </SelectLabel>
                    {lvHierarchy.parents.map(cat => (
                      <SelectItem key={cat.id} value={cat.category}>
                        <div className="flex items-center gap-2">
                          <CategoryIconRenderer category={cat.category} template={cat} size={18} />
                          <span>{cat.display_name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>

                  <SelectSeparator />

                  {/* Create New - single option */}
                  <SelectItem value="__create_new__" className="text-primary">
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      <span>New Category</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Subcategory dropdown - show when category is selected */}
            {selectedCategory && (
              <div className="space-y-2">
                <Label htmlFor="subcategory">Subcategory</Label>
                <Select
                  value={form.watch('subcategory') || '__none__'}
                  onValueChange={value => {
                    if (value === '__none__') {
                      form.setValue('subcategory', '');
                    } else if (value === '__create_new_sub__') {
                      const template = getCategoryTemplate(selectedCategory);
                      setCreateDomain(template?.domain || 'lighting_fixtures');
                      setCreateSubcategoryOpen(true);
                    } else {
                      form.setValue('subcategory', value);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select subcategory (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {subcategories.map(sub => (
                      <SelectItem key={sub.id} value={sub.subcategory || sub.category}>
                        <div className="flex items-center gap-2">
                          <CategoryIconRenderer category={sub.category} template={sub} size={16} />
                          <span>{sub.display_name}</span>
                        </div>
                      </SelectItem>
                    ))}
                    <SelectSeparator />
                    <SelectItem value="__create_new_sub__" className="text-primary">
                      <div className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        <span>Create New Subcategory</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...form.register('description')}
              rows={3}
              placeholder="Product description..."
              onKeyDown={handleInputKeyDown}
            />
          </div>
        </TabsContent>

        <TabsContent value="specs" className="space-y-6 pt-2">
          {/* Dimensions Section */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-muted-foreground">Dimensions</Label>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="width" className="text-xs">
                  Width
                </Label>
                <Input
                  id="width"
                  type="number"
                  step={form.watch('dimension_unit') === 'inches' ? '0.01' : '1'}
                  {...form.register('width', { valueAsNumber: true })}
                  onKeyDown={handleInputKeyDown}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="height" className="text-xs">
                  Height
                </Label>
                <Input
                  id="height"
                  type="number"
                  step={form.watch('dimension_unit') === 'inches' ? '0.01' : '1'}
                  {...form.register('height', { valueAsNumber: true })}
                  onKeyDown={handleInputKeyDown}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="depth" className="text-xs">
                  Depth
                </Label>
                <Input
                  id="depth"
                  type="number"
                  step={form.watch('dimension_unit') === 'inches' ? '0.01' : '1'}
                  {...form.register('depth', { valueAsNumber: true })}
                  onKeyDown={handleInputKeyDown}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dimension_unit" className="text-xs">
                  Unit
                </Label>
                <Select
                  value={form.watch('dimension_unit')}
                  onValueChange={handleDimensionUnitChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inches">inches</SelectItem>
                    <SelectItem value="mm">mm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="border-t" />

          {/* Weight & Power Section */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-muted-foreground">Weight & Power</Label>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="weight_value" className="text-xs">
                  Weight
                </Label>
                <Input
                  id="weight_value"
                  type="number"
                  step="0.1"
                  {...form.register('weight_value', { valueAsNumber: true })}
                  onKeyDown={handleInputKeyDown}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="weight_unit" className="text-xs">
                  Unit
                </Label>
                <Select value={form.watch('weight_unit')} onValueChange={handleWeightUnitChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="lbs">lbs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="power_consumption" className="text-xs">
                  Power (W)
                </Label>
                <Input
                  id="power_consumption"
                  type="number"
                  {...form.register('power_consumption', { valueAsNumber: true })}
                  onKeyDown={handleInputKeyDown}
                />
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Creating...' : 'Create Product'}
        </Button>
      </div>

      {/* Image Selector Modal */}
      {showImageSelector && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Select Product Image</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                {availableImages.length > 0
                  ? `${availableImages.length} image${availableImages.length > 1 ? 's' : ''} found`
                  : 'Add an image URL or search online'}
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Available Images Grid */}
              {availableImages.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-muted-foreground">Found Images</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {availableImages.map((image, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          onImageSelect?.(image);
                          setShowImageSelector(false);
                        }}
                        className={`relative aspect-square rounded-lg overflow-hidden border transition-all bg-muted/30 ${
                          selectedImage?.url === image.url
                            ? 'border-primary/60 ring-1 ring-primary/20'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={image.url}
                          alt={`Option ${index + 1}`}
                          className="w-full h-full object-contain p-2"
                          onError={e => {
                            logger.error('Failed to load option image:', image.url);
                            e.currentTarget.src = ImageProxyService.getPlaceholderImage();
                          }}
                        />
                        {selectedImage?.url === image.url && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {availableImages.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground font-medium">No images found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Enter a URL below or search online
                  </p>
                </div>
              )}

              {/* Divider */}
              {availableImages.length > 0 && <div className="border-t" />}

              {/* Custom URL Input */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-muted-foreground">
                  Add Custom Image
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="url"
                    placeholder="Paste image URL..."
                    value={customImageUrl}
                    onChange={e => setCustomImageUrl(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      if (customImageUrl) {
                        const customImage: ImageSearchResult = {
                          url: customImageUrl,
                          source: 'custom',
                          confidence: 1,
                          dimensions: { width: 800, height: 800 },
                          type: 'product',
                          angle: 'front',
                        };
                        onImageSelect?.(customImage);
                        setShowImageSelector(false);
                        setCustomImageUrl('');
                        toast.success('Image added');
                      } else {
                        toast.error('Please enter a valid image URL');
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>Search:</span>
                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(`${form.watch('brand')} ${form.watch('model')}`)}&tbm=isch`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Google Images
                  </a>
                  <span>•</span>
                  <a
                    href={`https://www.amazon.com/s?k=${encodeURIComponent(`${form.watch('brand')} ${form.watch('model')}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Amazon
                  </a>
                  <span>•</span>
                  <a
                    href={`https://www.bhphotovideo.com/c/search?q=${encodeURIComponent(`${form.watch('brand')} ${form.watch('model')}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    B&H Photo
                  </a>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-muted/30 flex justify-between">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  onImageSelect?.(null);
                  setShowImageSelector(false);
                }}
                className="text-muted-foreground"
              >
                Remove Image
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowImageSelector(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Category Dialog */}
      <QuickCategoryCreateDialog
        open={createCategoryOpen}
        onOpenChange={setCreateCategoryOpen}
        domain={createDomain}
        onCategoryCreated={newCategory => {
          queryClient.invalidateQueries({ queryKey: ['merged-categories'] });
          form.setValue('category', newCategory.category);
          setSelectedCategory(newCategory.category);
          form.setValue('subcategory', '');
        }}
        isSubcategory={false}
      />

      {/* Create Subcategory Dialog */}
      <QuickCategoryCreateDialog
        open={createSubcategoryOpen}
        onOpenChange={setCreateSubcategoryOpen}
        domain={createDomain}
        onCategoryCreated={newSubcategory => {
          queryClient.invalidateQueries({ queryKey: ['merged-categories'] });
          if (newSubcategory.subcategory) {
            form.setValue('subcategory', newSubcategory.subcategory);
          }
        }}
        isSubcategory={true}
        parentCategory={selectedCategory}
        parentCategories={allParentCategories.filter(c => !c.subcategory)}
      />
    </form>
  );
}
